import React, { useEffect, useMemo } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import { useLogistics, useRepairTrucksForTrucks } from '@/hooks/logistics/useLogistics'
import TransportTruckCard from '../cards/TransportTruckCard'
import { getTruckStatus } from '@otw/models/logistics'
import { useClientUsers } from 'hooks/useUsers'
import { usePickupLocations } from 'hooks/inventory/usePickupLocations'
import { useRepairShopLocations } from 'hooks/inventory/useRepairShopLocations'
import useAdminTransporter from 'hooks/admin/useAdminTransporter'

const OPTIC_FILTER_VALUE = 'OPTIC'
const ALL_STATUS_FILTER_VALUE = 'ALL'

interface TransportTrucksTabProps {
  searchTerm: string
  customerFilter: string
  statusFilter: string
  isActiveFilter: boolean
  flaggedOnly?: boolean
  focusTruckId?: string | null
  onFocusedTruckHandled?: () => void
}

const TransportTrucksTab: React.FC<TransportTrucksTabProps> = ({
  searchTerm,
  customerFilter,
  statusFilter,
  isActiveFilter,
  flaggedOnly = false,
  focusTruckId,
  onFocusedTruckHandled,
}) => {
  const {
    transportTrucksWithVehicles,
    isLoadingTransportTrucks,
  } = useLogistics()
  const { clientUsers } = useClientUsers()
  const { pickupLocations } = usePickupLocations()
  const { repairShopLocations } = useRepairShopLocations()
  const { transporterGroups = [], transporters = [] } = useAdminTransporter()
  const repairTrucksMap = useRepairTrucksForTrucks(transportTrucksWithVehicles.map((t) => t.truck.id))

  const normalizedSearch = searchTerm.trim().toLowerCase()
  const filteredTrucks = transportTrucksWithVehicles.filter(({ truck, vehicle, shipmentPlans }) => {
    const truckStatus = getTruckStatus(truck)

    const matchesActiveCompleted =
      isActiveFilter ? truckStatus !== 'delivered' : truckStatus === 'delivered'
    if (!matchesActiveCompleted) return false

    const matchesCustomer =
      customerFilter === 'ALL' ||
      (customerFilter === OPTIC_FILTER_VALUE &&
        shipmentPlans.some((plan) => !plan.client_user_id || plan.client_user_id === 'optic')) ||
      shipmentPlans.some((plan) => plan.client_user_id === customerFilter)

    if (!matchesCustomer) return false
    if (
      isActiveFilter &&
      statusFilter !== ALL_STATUS_FILTER_VALUE &&
      truckStatus !== statusFilter
    ) {
      return false
    }
    if (flaggedOnly) {
      const repairTruck = truck.currentLocationId
        ? repairTrucksMap.get(`${truck.id}_${truck.currentLocationId}`)
        : null
      if (!repairTruck?.flagged) return false
    }
    if (!normalizedSearch) return true

    const haystack = [
      truck.vin,
      vehicle?.make,
      vehicle?.model,
      vehicle?.year != null ? String(vehicle.year) : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()

    return haystack.includes(normalizedSearch)
  })

  const clientUsersById = useMemo(
    () => new Map(clientUsers.map((user) => [user.id, user])),
    [clientUsers]
  )
  const pickupLocationsById = useMemo(
    () =>
      new Map(
        (pickupLocations ?? [])
          .filter((location): location is typeof location & { id: string } => Boolean(location.id))
          .map((location) => [location.id, location])
      ),
    [pickupLocations]
  )
  const repairShopLocationsById = useMemo(
    () =>
      new Map(
        (repairShopLocations ?? [])
          .filter((location): location is typeof location & { id: string } => Boolean(location.id))
          .map((location) => [location.id, location])
      ),
    [repairShopLocations]
  )

  useEffect(() => {
    if (!focusTruckId) return
    const truckExists = transportTrucksWithVehicles.some(
      (entry) => entry.truck.id === focusTruckId,
    )
    if (!truckExists) {
      onFocusedTruckHandled?.()
      return
    }
    const el = document.getElementById(`transport-truck-card-${focusTruckId}`)
    if (!el) return
    el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    onFocusedTruckHandled?.()
  }, [focusTruckId, transportTrucksWithVehicles, onFocusedTruckHandled])

  if (isLoadingTransportTrucks) {
    return (
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (transportTrucksWithVehicles.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No trucks in the transport fleet yet. Click &quot;Add truck&quot; to add one.
        </Typography>
      </Box>
    )
  }

  if (filteredTrucks.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No trucks match the current search/filter.
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      {filteredTrucks.map((truckWithVehicle) => (
        <Box
          key={truckWithVehicle.truck.id}
          id={`transport-truck-card-${truckWithVehicle.truck.id}`}
          sx={{ pb: 2 }}
        >
          <TransportTruckCard
            truckWithVehicle={truckWithVehicle}
            repairTrucksMap={repairTrucksMap}
            clientUsersById={clientUsersById}
            pickupLocationsById={pickupLocationsById}
            repairShopLocationsById={repairShopLocationsById}
            transporterGroups={transporterGroups}
            transporters={transporters}
          />
        </Box>
      ))}
    </Box>
  )
}

export default TransportTrucksTab
